import { useCallback, useState, useEffect, useRef, useMemo } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  Background,
  Controls,
  MiniMap,
  MarkerType,
  applyNodeChanges,
  applyEdgeChanges,
  Panel,
} from '@xyflow/react'
import type { Node, Edge, NodeTypes, OnNodesChange, OnEdgesChange, EdgeTypes, OnNodesDelete } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import Dagre from '@dagrejs/dagre'
import { toast } from 'sonner'

import type { ActivityWithExchanges, ProjectExchange } from '@/lib/types/project-graph'
import type { ApiRequestError, DraggedComponentData } from '@/lib/types'
import { projectAPI } from '@/lib/api/projects'
import { ActivityNode } from './nodes/ActivityNode'
import { ExchangeNode } from './nodes/ExchangeNode'
import { FlowEdge } from './edges/FlowEdge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import { GitBranch, ChevronDown } from 'lucide-react'

interface ProjectGraphProps {
  activities: ActivityWithExchanges[]
  selectedNodeId?: string | null
  selectedNodeType?: 'activity' | 'exchange' | null
  onNodeClick?: (nodeId: string, nodeType: 'activity' | 'exchange') => void
  onDropOnActivity?: (activityId: string, dragData: DraggedComponentData) => void
  /** Called after a node deletion is persisted (or fails) so the parent can refresh. */
  onGraphMutated?: () => void
  /** Spectator mode: hides controls, minimap, layout selector. Uses vertical layout. */
  spectatorMode?: boolean
}

const nodeTypes: NodeTypes = {
  activity: ActivityNode,
  exchange: ExchangeNode,
}

const edgeTypes: EdgeTypes = {
  flow: FlowEdge,
}

// Node dimensions for layout calculation
const NODE_DIMENSIONS = {
  activity: { width: 280, height: 120 },
  exchange: { width: 200, height: 70 },
}

// Layout algorithm types
type LayoutAlgorithm = 'dagre-lr' | 'dagre-tb'

const LAYOUT_OPTIONS: {
  value: LayoutAlgorithm
  label: string
  icon: typeof GitBranch
  description: string
}[] = [
  {
    value: 'dagre-lr',
    label: 'Horizontal Flow',
    icon: GitBranch,
    description: 'Left to right hierarchy',
  },
  {
    value: 'dagre-tb',
    label: 'Vertical Flow',
    icon: GitBranch,
    description: 'Top to bottom hierarchy',
  },
]

// ============================================================================
// LAYOUT ALGORITHMS
// ============================================================================

function applyDagreLayout(nodes: Node[], edges: Edge[], direction: 'LR' | 'TB' = 'LR'): Node[] {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}))

  g.setGraph({
    rankdir: direction,
    nodesep: 80,
    ranksep: 150,
    marginx: 50,
    marginy: 50,
  })

  nodes.forEach((node) => {
    const dims = node.type === 'activity' ? NODE_DIMENSIONS.activity : NODE_DIMENSIONS.exchange
    g.setNode(node.id, { width: dims.width, height: dims.height })
  })

  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target)
  })

  Dagre.layout(g)

  return nodes.map((node) => {
    const nodeWithPosition = g.node(node.id)
    const dims = node.type === 'activity' ? NODE_DIMENSIONS.activity : NODE_DIMENSIONS.exchange

    return {
      ...node,
      position: {
        x: nodeWithPosition.x - dims.width / 2,
        y: nodeWithPosition.y - dims.height / 2,
      },
    }
  })
}

// ============================================================================
// FU SCALING
// ============================================================================

// Walk the internal-link DAG (custom-activity -> custom-activity edges) and
// return scalingFactor[activity.id] such that every authored exchange amount
// of activity X, multiplied by scalingFactor[X], gives the FU-propagated
// amount in the graph. Roots (activities not consumed by any other custom
// activity) get scaling = 1 / production_amount so their production renders
// as 1 unit of FU. Returns null when a cycle is detected — caller falls back
// to recipe view (scaling = 1 everywhere).
function computeScalingFactors(
  activities: ActivityWithExchanges[],
): { scaling: Map<string, number>; rootIds: string[] } | null {
  const byId = new Map<string, ActivityWithExchanges>()
  const codeToId = new Map<string, string>()
  activities.forEach((a) => {
    byId.set(a.id, a)
    codeToId.set(a.code, a.id)
  })

  const productionAmount = (a: ActivityWithExchanges): number => {
    const prod = a.exchanges.filter((e) => e.exchange_type === 'production')
    if (prod.length === 0) return 1
    const sum = prod.reduce((acc, e) => acc + e.amount, 0)
    return sum === 0 ? 1 : sum
  }

  const consumedBy = new Set<string>()
  activities.forEach((a) => {
    a.exchanges.forEach((e) => {
      if (
        e.exchange_type === 'technosphere' &&
        e.input_activity_code &&
        (e.input_database === 'custom' || !e.input_database)
      ) {
        const upstreamId = codeToId.get(e.input_activity_code)
        if (upstreamId) consumedBy.add(upstreamId)
      }
    })
  })

  const rootIds = activities.filter((a) => !consumedBy.has(a.id)).map((a) => a.id)

  const scaling = new Map<string, number>()
  activities.forEach((a) => scaling.set(a.id, 0))

  const visiting = new Set<string>()
  let cycle = false

  const visit = (activityId: string): void => {
    if (cycle) return
    if (visiting.has(activityId)) {
      cycle = true
      return
    }
    visiting.add(activityId)
    const activity = byId.get(activityId)
    if (!activity) {
      visiting.delete(activityId)
      return
    }
    const downstreamScale = scaling.get(activityId) || 0
    activity.exchanges.forEach((e) => {
      if (
        e.exchange_type !== 'technosphere' ||
        !e.input_activity_code ||
        (e.input_database && e.input_database !== 'custom')
      ) {
        return
      }
      const upstreamId = codeToId.get(e.input_activity_code)
      if (!upstreamId) return
      const upstream = byId.get(upstreamId)
      if (!upstream) return
      const upstreamProd = productionAmount(upstream)
      const contribution = (downstreamScale * e.amount) / upstreamProd
      scaling.set(upstreamId, (scaling.get(upstreamId) || 0) + contribution)
      visit(upstreamId)
    })
    visiting.delete(activityId)
  }

  rootIds.forEach((rootId) => {
    const root = byId.get(rootId)
    if (!root) return
    scaling.set(rootId, 1 / productionAmount(root))
    visit(rootId)
  })

  if (cycle) return null
  return { scaling, rootIds }
}

// ============================================================================
// GRAPH BUILDING
// ============================================================================

function buildGraphFromActivities(
  activities: ActivityWithExchanges[],
  layoutAlgorithm: LayoutAlgorithm = 'dagre-lr',
  selectedNodeId?: string | null,
  selectedNodeType?: 'activity' | 'exchange' | null,
): {
  nodes: Node[]
  edges: Edge[]
  fuInfo: { rootIds: string[]; cycle: boolean }
} {
  const nodes: Node[] = []
  const edges: Edge[] = []

  const scalingResult = computeScalingFactors(activities)
  if (!scalingResult) {
    // Cycle detected — fall back to recipe view; warn once per build.
    console.warn(
      '[ProjectGraph] Cycle detected in custom-activity graph; falling back to recipe view (no FU scaling).',
    )
  }
  const scaling = scalingResult?.scaling ?? new Map<string, number>()
  const fuInfo = {
    rootIds: scalingResult?.rootIds ?? [],
    cycle: scalingResult === null,
  }
  const scaleOf = (activityId: string): number =>
    scalingResult ? (scaling.get(activityId) ?? 1) : 1

  // Build a map of activity codes to activity IDs for quick lookup
  const activityCodeToId = new Map<string, string>()
  activities.forEach((activity) => {
    activityCodeToId.set(activity.code, activity.id)
  })

  // Track which exchanges link to internal activities
  const internalLinkExchangeIds = new Set<string>()
  const crossProjectExchangeIds = new Set<string>()

  activities.forEach((activity) => {
    activity.exchanges.forEach((exchange) => {
      if (exchange.exchange_type === 'technosphere' && exchange.input_activity_code) {
        if (
          (exchange.input_database === 'custom' || !exchange.input_database) &&
          activityCodeToId.has(exchange.input_activity_code)
        ) {
          internalLinkExchangeIds.add(exchange.id)
        } else if (exchange.input_database?.startsWith('project:')) {
          crossProjectExchangeIds.add(exchange.id)
        }
      }
    })
  })

  // Initial positions (will be overwritten by layout)
  const HORIZONTAL_SPACING = 400
  const ACTIVITY_HEIGHT = 120
  const ACTIVITY_WIDTH = 280
  const EXCHANGE_NODE_HEIGHT = 60

  // Build nodes and edges
  activities.forEach((activity, actIdx) => {
    const activityX = actIdx * HORIZONTAL_SPACING
    const activityY = 200

    const isActivitySelected = selectedNodeType === 'activity' && selectedNodeId === activity.id
    nodes.push({
      id: `activity-${activity.id}`,
      type: 'activity',
      position: { x: activityX, y: activityY },
      data: {
        activity,
        label: activity.name,
        selected: isActivitySelected,
      },
    })

    const technosphere = activity.exchanges.filter((e) => e.exchange_type === 'technosphere')
    const biosphere = activity.exchanges.filter((e) => e.exchange_type === 'biosphere')
    const production = activity.exchanges.filter((e) => e.exchange_type === 'production')

    const ownerScale = scaleOf(activity.id)

    let externalExchangeIndex = 0
    technosphere.forEach((exchange) => {
      if (internalLinkExchangeIds.has(exchange.id)) {
        const sourceActivityId = activityCodeToId.get(exchange.input_activity_code!)
        if (sourceActivityId) {
          edges.push({
            id: `flow-${exchange.id}`,
            source: `activity-${sourceActivityId}`,
            target: `activity-${activity.id}`,
            sourceHandle: 'right',
            targetHandle: 'left',
            type: 'flow',
            animated: true,
            data: {
              exchange,
              amount: exchange.amount * ownerScale,
              unit: exchange.unit,
            },
            style: { stroke: '#8b5cf6', strokeWidth: 3 },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#8b5cf6' },
          })
        }
      } else {
        const isCrossProject = crossProjectExchangeIds.has(exchange.id)
        const nodeId = `exchange-${exchange.id}`
        const yOffset = -100 - externalExchangeIndex * (EXCHANGE_NODE_HEIGHT + 20)
        externalExchangeIndex++

        const isExchangeSelected = selectedNodeType === 'exchange' && selectedNodeId === exchange.id
        nodes.push({
          id: nodeId,
          type: 'exchange',
          position: { x: activityX - 50, y: activityY + yOffset },
          data: {
            exchange,
            exchangeType: isCrossProject ? ('cross-project' as const) : ('technosphere' as const),
            selected: isExchangeSelected,
            displayAmount: exchange.amount * ownerScale,
          },
        })

        const edgeColor = isCrossProject ? '#ec4899' : '#3b82f6'
        edges.push({
          id: `edge-${exchange.id}`,
          source: nodeId,
          target: `activity-${activity.id}`,
          sourceHandle: 'bottom',
          targetHandle: 'top',
          type: 'smoothstep',
          animated: isCrossProject,
          style: {
            stroke: edgeColor,
            strokeWidth: 2,
            strokeDasharray: isCrossProject ? '5 5' : undefined,
          },
          markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor },
        })
      }
    })

    biosphere.forEach((exchange, idx) => {
      const nodeId = `exchange-${exchange.id}`
      const yOffset = ACTIVITY_HEIGHT + 40 + idx * (EXCHANGE_NODE_HEIGHT + 20)
      const isExchangeSelected = selectedNodeType === 'exchange' && selectedNodeId === exchange.id

      nodes.push({
        id: nodeId,
        type: 'exchange',
        position: { x: activityX + 50, y: activityY + yOffset },
        data: {
          exchange,
          exchangeType: 'biosphere' as const,
          selected: isExchangeSelected,
          displayAmount: exchange.amount * ownerScale,
        },
      })

      edges.push({
        id: `edge-${exchange.id}`,
        source: `activity-${activity.id}`,
        target: nodeId,
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#22c55e', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#22c55e' },
      })
    })

    const hasOutgoingInternalLinks = activities.some((otherActivity) =>
      otherActivity.exchanges.some(
        (e) =>
          internalLinkExchangeIds.has(e.id) &&
          activityCodeToId.get(e.input_activity_code!) === activity.id,
      ),
    )

    if (!hasOutgoingInternalLinks) {
      production.forEach((exchange, idx) => {
        const nodeId = `exchange-${exchange.id}`
        const isExchangeSelected = selectedNodeType === 'exchange' && selectedNodeId === exchange.id

        nodes.push({
          id: nodeId,
          type: 'exchange',
          position: {
            x: activityX + ACTIVITY_WIDTH + 60,
            y: activityY + 30 + idx * (EXCHANGE_NODE_HEIGHT + 20),
          },
          data: {
            exchange,
            exchangeType: 'production' as const,
            selected: isExchangeSelected,
            displayAmount: exchange.amount * ownerScale,
          },
        })

        edges.push({
          id: `edge-${exchange.id}`,
          source: `activity-${activity.id}`,
          target: nodeId,
          sourceHandle: 'right',
          targetHandle: 'left',
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#f59e0b', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#f59e0b' },
        })
      })
    }
  })

  // Apply selected layout algorithm
  const layoutedNodes: Node[] =
    layoutAlgorithm === 'dagre-tb'
      ? applyDagreLayout(nodes, edges, 'TB')
      : applyDagreLayout(nodes, edges, 'LR')

  return { nodes: layoutedNodes, edges, fuInfo }
}

// ============================================================================
// POSITION PERSISTENCE
// ============================================================================

type SavedPositions = Record<string, { x: number; y: number }>

function getPositionStorageKey(projectId: string): string {
  return `workspace-positions-${projectId}`
}

function loadSavedPositions(projectId: string): SavedPositions | null {
  try {
    const raw = localStorage.getItem(getPositionStorageKey(projectId))
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function saveSavedPositions(projectId: string, nodes: Node[]): void {
  try {
    const positions: SavedPositions = {}
    nodes.forEach((n) => {
      positions[n.id] = n.position
    })
    localStorage.setItem(getPositionStorageKey(projectId), JSON.stringify(positions))
  } catch {
    /* quota exceeded etc */
  }
}

function clearSavedPositions(projectId: string): void {
  try {
    localStorage.removeItem(getPositionStorageKey(projectId))
  } catch {
    /* ignore */
  }
}

// Generate a fingerprint of activities for comparison
function getActivitiesFingerprint(activities: ActivityWithExchanges[]): string {
  return activities
    .map((a) => {
      const exchangeIds = a.exchanges
        .map((e) => e.id)
        .sort()
        .join(',')
      return `${a.id}:${exchangeIds}`
    })
    .sort()
    .join('|')
}

// ============================================================================
// Auto fit-view when nodes change (spectator mode)
function FitViewOnChange({ nodeCount }: { nodeCount: number }) {
  const { fitView } = useReactFlow()
  const prevCount = useRef(nodeCount)

  useEffect(() => {
    if (nodeCount !== prevCount.current) {
      prevCount.current = nodeCount
      // Small delay to let layout settle
      const t = setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 100)
      return () => clearTimeout(t)
    }
  }, [nodeCount, fitView])

  return null
}

// MAIN COMPONENT
// ============================================================================

export function ProjectGraph(props: ProjectGraphProps) {
  return (
    <ReactFlowProvider>
      <ProjectGraphInner {...props} />
    </ReactFlowProvider>
  )
}

function ProjectGraphInner({
  activities,
  selectedNodeId,
  selectedNodeType,
  onNodeClick,
  onDropOnActivity,
  onGraphMutated,
  spectatorMode = false,
}: ProjectGraphProps) {
  const [layoutAlgorithm, setLayoutAlgorithm] = useState<LayoutAlgorithm>('dagre-lr')

  const [nodes, setNodes] = useState<Node[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const [fuInfo, setFuInfo] = useState<{ rootIds: string[]; cycle: boolean }>({
    rootIds: [],
    cycle: false,
  })

  const { screenToFlowPosition, fitView } = useReactFlow()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const lastContainerWidthRef = useRef(0)

  const lastFingerprintRef = useRef<string>('')
  const lastLayoutRef = useRef<LayoutAlgorithm>(layoutAlgorithm)
  const lastLayoutVersionRef = useRef(0)
  const lastSelectedNodeIdRef = useRef<string | null | undefined>(selectedNodeId)
  const prevNodeIdsRef = useRef<Set<string>>(new Set())
  const newNodeIdsRef = useRef<Set<string>>(new Set())
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const projectId = useMemo(() => activities[0]?.project_id || '', [activities])

  // When true, the next rebuild should ignore saved positions (user picked a layout)
  const forceLayoutRef = useRef(false)
  const [layoutVersion, setLayoutVersion] = useState(0)

  // Sync state when activities change or layout changes
  useEffect(() => {
    const newFingerprint = getActivitiesFingerprint(activities)

    if (
      newFingerprint !== lastFingerprintRef.current ||
      layoutAlgorithm !== lastLayoutRef.current ||
      layoutVersion !== lastLayoutVersionRef.current
    ) {
      const {
        nodes: newNodes,
        edges: newEdges,
        fuInfo: newFuInfo,
      } = buildGraphFromActivities(activities, layoutAlgorithm, selectedNodeId, selectedNodeType)
      setFuInfo(newFuInfo)

      // Apply saved positions (unless user explicitly picked a layout)
      if (forceLayoutRef.current) {
        // User clicked a layout — clear saved positions, use computed layout
        if (projectId) clearSavedPositions(projectId)
        forceLayoutRef.current = false
      } else if (projectId) {
        const saved = loadSavedPositions(projectId)
        if (saved) {
          newNodes.forEach((n) => {
            if (saved[n.id]) {
              n.position = saved[n.id]
            }
          })
        }
      }

      // Detect new node IDs for highlight (only in spectator mode, skip first render)
      if (spectatorMode && prevNodeIdsRef.current.size > 0) {
        const currentIds = new Set(newNodes.map((n) => n.id))
        const added = new Set<string>()
        currentIds.forEach((id) => {
          if (!prevNodeIdsRef.current.has(id)) added.add(id)
        })
        newNodeIdsRef.current = added

        // Mark new nodes with isNew flag
        if (added.size > 0) {
          newNodes.forEach((n) => {
            if (added.has(n.id)) {
              n.data = { ...n.data, isNew: true }
            }
          })

          // Clear highlight after animation (2s)
          if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
          highlightTimerRef.current = setTimeout(() => {
            newNodeIdsRef.current = new Set()
            setNodes((prev) =>
              prev.map((n) => (n.data.isNew ? { ...n, data: { ...n.data, isNew: false } } : n)),
            )
          }, 2200)
        }
      }

      // Update previous node IDs for next comparison
      prevNodeIdsRef.current = new Set(newNodes.map((n) => n.id))

      setNodes(newNodes)
      setEdges(newEdges)
      lastFingerprintRef.current = newFingerprint
      lastLayoutRef.current = layoutAlgorithm
      lastLayoutVersionRef.current = layoutVersion
    }
  }, [
    activities,
    layoutAlgorithm,
    layoutVersion,
    selectedNodeId,
    selectedNodeType,
    spectatorMode,
    projectId,
  ])

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [])

  // Update selection state without rebuilding entire graph
  useEffect(() => {
    if (selectedNodeId !== lastSelectedNodeIdRef.current) {
      setNodes((currentNodes) =>
        currentNodes.map((node) => {
          const isActivity = node.id.startsWith('activity-')
          const nodeRealId = isActivity
            ? node.id.replace('activity-', '')
            : node.id.replace('exchange-', '')
          const isSelected = isActivity
            ? selectedNodeType === 'activity' && selectedNodeId === nodeRealId
            : selectedNodeType === 'exchange' && selectedNodeId === nodeRealId

          return {
            ...node,
            data: { ...node.data, selected: isSelected },
          }
        }),
      )
      lastSelectedNodeIdRef.current = selectedNodeId
    }
  }, [selectedNodeId, selectedNodeType])

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => {
        const updated = applyNodeChanges(changes, nds)
        // Debounce-save positions when user drags nodes
        const hasDrag = changes.some((c) => c.type === 'position' && !c.dragging)
        if (hasDrag && projectId) {
          if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
          saveTimerRef.current = setTimeout(() => {
            saveSavedPositions(projectId, updated)
          }, 300)
        }
        return updated
      })
    },
    [projectId],
  )

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  )

  const onNodesDelete: OnNodesDelete = useCallback(
    async (deleted) => {
      if (spectatorMode || deleted.length === 0) return

      const results = await Promise.allSettled(
        deleted.map(async (node) => {
          if (node.id.startsWith('exchange-')) {
            const exchange = node.data?.exchange as ProjectExchange | undefined
            if (!exchange?.activity_id || !exchange?.id) {
              throw new Error('Exchange node missing activity_id/id')
            }
            await projectAPI.deleteExchange(exchange.activity_id, exchange.id)
            return { label: `Exchange "${exchange.name ?? exchange.id}"` }
          }
          if (node.id.startsWith('activity-')) {
            const activityId = node.id.replace('activity-', '')
            await projectAPI.deleteActivity(activityId)
            const activity = node.data?.activity as { name?: string } | undefined
            return { label: `Activity "${activity?.name ?? activityId}"` }
          }
          throw new Error(`Unknown node id: ${node.id}`)
        }),
      )

      let okCount = 0
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          okCount++
        } else {
          const reason = r.reason as ApiRequestError
          const rawDetail = reason?.response?.data?.detail
          const detail = typeof rawDetail === 'string' ? rawDetail : rawDetail?.message
          toast.error(detail || reason?.message || 'Failed to delete node')
        }
      })
      if (okCount > 0) toast.success(okCount === 1 ? 'Deleted' : `Deleted ${okCount} items`)

      // Refresh from server so optimistic removals reconcile with persisted state
      // (puts back any node whose API delete failed).
      onGraphMutated?.()
    },
    [spectatorMode, onGraphMutated],
  )

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (!onNodeClick) return

      if (node.id.startsWith('activity-')) {
        onNodeClick(node.id.replace('activity-', ''), 'activity')
      } else if (node.id.startsWith('exchange-')) {
        onNodeClick(node.id.replace('exchange-', ''), 'exchange')
      }
    },
    [onNodeClick],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (!onDropOnActivity) return

      const raw = e.dataTransfer.getData('application/json')
      if (!raw) return

      let dragData: DraggedComponentData
      try {
        dragData = JSON.parse(raw)
      } catch {
        return
      }

      // Find which activity node the drop landed on
      const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
      const activityNode = nodes.find((n) => {
        if (n.type !== 'activity') return false
        const dims = NODE_DIMENSIONS.activity
        return (
          flowPos.x >= n.position.x &&
          flowPos.x <= n.position.x + dims.width &&
          flowPos.y >= n.position.y &&
          flowPos.y <= n.position.y + dims.height
        )
      })

      if (activityNode) {
        const activityId = activityNode.id.replace('activity-', '')
        onDropOnActivity(activityId, dragData)
      }
    },
    [onDropOnActivity, screenToFlowPosition, nodes],
  )

  const currentLayoutOption =
    LAYOUT_OPTIONS.find((o) => o.value === layoutAlgorithm) || LAYOUT_OPTIONS[0]

  const fuBadge = useMemo(() => {
    if (fuInfo.cycle) {
      return { label: 'Recipe view (cycle detected — unscaled)', tone: 'warn' as const }
    }
    if (fuInfo.rootIds.length === 0) return null
    const roots = fuInfo.rootIds
      .map((id) => activities.find((a) => a.id === id))
      .filter((a): a is ActivityWithExchanges => !!a)
    if (roots.length === 0) return null
    if (roots.length === 1) {
      const r = roots[0]
      return {
        label: `Scaled to FU = 1 ${r.unit} of ${r.name}`,
        tone: 'info' as const,
      }
    }
    return {
      label: `Scaled to FU = 1 unit each of ${roots.length} roots`,
      tone: 'info' as const,
    }
  }, [fuInfo, activities])

  // Auto fit-view when container transitions from hidden (0 width) to visible.
  // CenterPanel keeps all tabs mounted and toggles visibility via `display: none`,
  // so the initial `fitView` prop fires while the container is 0×0 and produces
  // a blank/cropped graph. Watch the container size and re-fit on the 0→>0 edge.
  // Declared before the early return below so hook order stays stable.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const width = entry.contentRect.width
      const prevWidth = lastContainerWidthRef.current
      lastContainerWidthRef.current = width
      if (prevWidth === 0 && width > 0 && nodes.length > 0) {
        // Defer a tick so ReactFlow picks up the new viewport dimensions.
        requestAnimationFrame(() => fitView({ padding: 0.2, duration: 200 }))
      }
    })

    observer.observe(el)
    return () => observer.disconnect()
  }, [fitView, nodes.length])

  if (activities.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p>No activities to display. Select a project to visualize.</p>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="w-full h-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodesDelete={onNodesDelete}
        onNodeClick={handleNodeClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={!spectatorMode}
        nodesConnectable={false}
        elementsSelectable={!spectatorMode}
        deleteKeyCode={spectatorMode ? null : ['Delete', 'Backspace']}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#334155" gap={20} />
        {fuBadge && (
          <Panel position="top-left">
            <div
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium shadow border ${
                fuBadge.tone === 'warn'
                  ? 'bg-amber-500/20 border-amber-400/50 text-amber-100'
                  : 'bg-slate-800/80 border-slate-600/60 text-slate-200'
              }`}
            >
              {fuBadge.label}
            </div>
          </Panel>
        )}
        {spectatorMode && <FitViewOnChange nodeCount={nodes.length} />}
        {!spectatorMode && <Controls />}
        {!spectatorMode && (
          <MiniMap
            nodeColor={(node) => {
              if (node.type === 'activity') return '#6366f1'
              const data = node.data as { exchangeType?: string }
              if (data?.exchangeType === 'technosphere') return '#3b82f6'
              if (data?.exchangeType === 'biosphere') return '#22c55e'
              if (data?.exchangeType === 'cross-project') return '#ec4899'
              return '#f59e0b'
            }}
            maskColor="rgba(0, 0, 0, 0.8)"
          />
        )}
        {!spectatorMode && (
          <Panel position="top-right">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm" className="flex items-center gap-2">
                  <currentLayoutOption.icon className="w-4 h-4" />
                  <span>{currentLayoutOption.label}</span>
                  <ChevronDown className="w-3 h-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Layout Algorithm</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {LAYOUT_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => {
                      forceLayoutRef.current = true
                      setLayoutAlgorithm(option.value)
                      setLayoutVersion((v) => v + 1)
                    }}
                    className="flex items-center gap-2"
                  >
                    <option.icon className="w-4 h-4" />
                    <div className="flex flex-col">
                      <span className={layoutAlgorithm === option.value ? 'font-medium' : ''}>
                        {option.label}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {option.description}
                      </span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </Panel>
        )}
        {spectatorMode && (
          <Panel position="bottom-right">
            <div className="flex items-center gap-1.5 opacity-40 hover:opacity-60 transition-opacity pointer-events-none select-none">
              <img src="/logo-square.svg" alt="" className="w-4 h-4" />
              <span className="text-[10px] text-slate-400 font-medium tracking-wide">
                Powered by EcoSemantic
              </span>
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  )
}
